import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { isExternal } from "../lib/siteNav";

// Announcement bar (EP-001 v1.1, D24). Controlled in Sanity (Navigation document:
// "Show Announcement Banner", text and URL) and off by default; when off it renders
// nothing. One short message, an optional link, and a close button that remembers the
// visitor's choice for that message. Calm and informational, never promotional.

const STORAGE_KEY = "el-announcement-dismissed";

const readDismissed = () => {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

export const AnnouncementBar = ({ enabled, text, url }) => {
    const message = (text || "").trim();
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(Boolean(message) && readDismissed() === message);
    }, [message]);

    if (!enabled || !message || dismissed) return null;

    const dismiss = () => {
        setDismissed(true);
        try {
            localStorage.setItem(STORAGE_KEY, message);
        } catch {
            /* storage unavailable: hide for this visit only */
        }
    };

    const linkClass = "font-semibold underline underline-offset-2";
    const link = url?.trim()
        ? isExternal(url)
            ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className={linkClass}>
                      Learn more<span className="sr-only"> (opens in a new tab)</span>
                  </a>
              )
            : (
                  <Link to={url} className={linkClass}>
                      Learn more
                  </Link>
              )
        : null;

    return (
        <div data-testid="announcement-bar" className="border-b border-brand-line bg-brand-surface">
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-2 sm:px-8">
                <p className="text-sm text-brand-text">
                    {message} {link}
                </p>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss announcement"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-control text-brand-muted transition-colors duration-150 hover:text-brand-text"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};
